import React, { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { equals, isEmpty } from 'ramda'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import {
  Icon,
  Chip,
  Grid,
  Popover,
  FormGroup,
  FormControl,
  FormControlLabel,
  makeStyles,
  Typography,
} from '@material-ui/core'

import { SUBSCRIBER_WITHOUT_GROUP } from 'app/constants/recipient'
import CheckBox from 'app/componentV4/CheckBox'

import { handleOptionsFilterGroups, reduceArrToObj } from './helper'
import { GROUP_CREATED_BY_USER } from 'libs/groups.utils'
import { getGroupOptions } from 'commons/groupLogic'
import useSelectGroup from 'app/main/subscribersV4/useSelectGroup'
import GroupSelectorContent from 'app/main/subscribersV4/components/GroupSelectorContent'
import { useFlag } from '@unleash/proxy-client-react'

const useStyles = makeStyles((theme) => ({
  chip: {
    border: `1px solid ${theme.colors['primary-200']}`,
    '&.MuiChip-clickable:hover': {
      border: `1px solid ${theme.colors['primary-350']}`,
    },
  },
  outlinedChip: {
    textTransform: 'none',
    padding: '0.75rem 1.25rem',
    color: theme.colors['grey-200'],
    border: '1px solid',
    borderColor: theme.colors['grey-200'],
    height: '3.7rem',
    maxWidth: '250px',
    minWidth: '250px',
    fontSize: '1.4rem',
    borderRadius: '4px',
    backgroundColor: 'white !important',
    '&:hover': {
      backgroundColor: `theme.colors['grey-200'] !important`,
    },
    '&:action': {
      backgroundColor: `theme.colors['grey-200'] !important`,
    },
  },
  paper: {
    maxHeight: 'calc(100% - 400px)',
    padding: '1.25rem',
  },
}))

const PAGE_LIMIT = 20

function LegacyGroupsSelector(props) {
  const {
    onChange,
    disabled = false,
    className,
    name,
    value,
    isImportedPage = false,
  } = props
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const groups = useSelector((state) => state.groups.groupList)

  const [isOpen, setIsOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selected, setSelected] = useState({ groups: [], tags: {} })
  const [optionsArr, setOptionsArr] = useState([])
  const [optionsObj, setOptionsObj] = useState({})
  const [countSelected, setCountSelected] = useState(0)
  const [isSelectedAll, setIsSelectedAll] = useState(false)
  const [initialOptionsObj, setInitialOptionsObj] = useState({})

  const handleLabel = useCallback(() => {
    const onlyUserGroups = groups?.filter(
      (group) => group.type === GROUP_CREATED_BY_USER,
    )
    if (!onlyUserGroups?.length && isImportedPage) {
      return formatMessage({
        id: 'group.notAvailable',
      })
    }
    if (isSelectedAll || countSelected === 0) {
      return isImportedPage
        ? formatMessage({
            id: 'subscriber.importCsv.group.label',
          })
        : formatMessage({
            id: 'statuse.all',
          })
    } else {
      let results = []

      for (const { id, name, tags } of optionsArr) {
        const parent = optionsObj[id]
        if (parent.parentChecked) {
          results.push(name)
        } else if (!parent.parentChecked && parent.indeterminateParent) {
          for (const { id: tagId, name } of tags || []) {
            if (optionsObj[id]['tags'][tagId].childrenChecked) {
              results.push(name)
            }
            if (results.length === 2) break
          }
        }
        if (results.length === 2) break
      }
      return results.length === 1 ? results[0] : `${results[0]}...`
    }
  }, [
    countSelected,
    formatMessage,
    isImportedPage,
    isSelectedAll,
    optionsArr,
    optionsObj,
  ])

  const handleOnChange = useCallback((updatedOptionsObj) => {
    if (isEmpty(updatedOptionsObj)) return
    const result = Object.entries(updatedOptionsObj).reduce(
      (acc, [parentId, parentData]) => {
        if (parentData.parentChecked) {
          acc.groups.push(parentId)
        }
        const childIds = Object.entries(parentData.tags)
          .filter(([_, tag]) => tag.childrenChecked)
          .map(([childId]) => childId)
        if (childIds.length > 0) {
          acc.tags[parentId] = childIds
        }
        return acc
      },
      { groups: [], tags: {} },
    )
    setSelected(result)
  }, [])

  const handleParentCheckboxChange = (parentKey, bool) => {
    return {
      ...optionsObj[parentKey],
      parentChecked: bool,
      indeterminateParent: false,
      tags: Object.keys(optionsObj[parentKey]?.tags).reduce((tgs, tagKey) => {
        tgs[tagKey] = {
          ...optionsObj[parentKey]?.tags[tagKey],
          childrenChecked: bool,
        }
        return tgs
      }, {}),
    }
  }

  const checkSelectedAllParent = (childrens, bool) => {
    const countChild = Object.values(childrens).length ?? 0
    let countSelectedChild =
      Object.values(childrens).filter((children) => children.childrenChecked)
        .length ?? 0
    const counting = bool ? ++countSelectedChild : --countSelectedChild
    return { isSelectedAllParent: counting === countChild, countSelectedChild }
  }

  const handleSelectedChild = (parentKey, childKey, bool) => {
    const { isSelectedAllParent, countSelectedChild } = checkSelectedAllParent(
      optionsObj[parentKey].tags,
      bool,
    )

    const selectedChild = {
      ...optionsObj,
      [parentKey]: {
        ...optionsObj[parentKey],
        parentChecked: isSelectedAllParent,
        indeterminateParent: countSelectedChild > 0 && !isSelectedAllParent,
        tags: {
          ...optionsObj[parentKey].tags,
          [childKey]: {
            ...optionsObj[parentKey]?.tags[childKey],
            childrenChecked: bool,
          },
        },
      },
    }
    setOptionsObj(selectedChild)
    handleOnChange(selectedChild)
  }

  const handleSelectedParent = (parentKey, bool) => {
    const selectedParent = {
      ...optionsObj,
      [parentKey]: handleParentCheckboxChange(parentKey, bool),
    }
    setOptionsObj(selectedParent)
    handleOnChange(selectedParent)
  }

  const handleSelectedAll = (bool) => {
    const selectedAll = Object.keys(optionsObj).reduce((prt, parentKey) => {
      prt[parentKey] = handleParentCheckboxChange(parentKey, bool)
      return prt
    }, {})
    setOptionsObj(selectedAll)
    handleOnChange(selectedAll)
  }

  const handleOnClose = useCallback(() => {
    !equals(value, selected) && onChange(selected)
    setIsOpen(false)
    setAnchorEl(null)
  }, [onChange, selected, value])

  useEffect(() => {
    const formattedGroup = getGroupOptions(formatMessage, groups)
    const renewGroups = handleOptionsFilterGroups(formattedGroup)
    setOptionsArr(renewGroups)
    const obj = reduceArrToObj(renewGroups)
    setOptionsObj(obj)
    setInitialOptionsObj(obj)
  }, [formatMessage, groups])

  useEffect(() => {
    const countAll = Object.values(optionsObj).filter(
      (parent) =>
        parent.parentChecked ||
        Object.values(parent.tags).filter((child) => child.childrenChecked)
          .length,
    ).length
    setCountSelected(countAll)
    setIsSelectedAll(countAll === Object.keys(optionsObj).length)
  }, [optionsObj])

  useEffect(() => {
    if (isEmpty(value.groups) && isEmpty(value.tags)) {
      setOptionsObj(initialOptionsObj)
    }
  }, [initialOptionsObj, value])

  return (
    <>
      {isEmpty(groups) || isEmpty(optionsObj) ? null : (
        <>
          {isImportedPage ? (
            <div className="grid gap-2">
              <div className="text-primary text-14 font-semibold">
                {formatMessage({ id: 'subscriber.groupsSubgroups.title' })}
              </div>
              <Chip
                className={clsx(classes.outlinedChip)}
                label={
                  <Typography className="flex items-center gap-2">
                    <div className="mx-3 font-medium">{handleLabel()}</div>
                    <Icon fontSize="small" className="text-primary">
                      {isOpen ? 'expand_less' : 'expand_more'}
                    </Icon>
                  </Typography>
                }
                onClick={(event) => {
                  setIsOpen(true)
                  setAnchorEl(event.currentTarget)
                }}
                disabled={disabled}
              />
            </div>
          ) : (
            <Chip
              className={clsx(classes.chip, className)}
              label={
                <Typography className="flex items-center">
                  {formatMessage({ id: 'subscriber.label.groups' })}:
                  <strong className="mx-3">{handleLabel()}</strong>
                  <Icon fontSize="small" className="text-primary">
                    {isOpen ? 'expand_less' : 'expand_more'}
                  </Icon>
                </Typography>
              }
              onClick={(event) => {
                setIsOpen(true)
                setAnchorEl(event.currentTarget)
              }}
              disabled={disabled}
            />
          )}

          <Popover
            id={`${name}-legacy-groups-selector-popover`}
            open={isOpen}
            anchorEl={anchorEl}
            onClose={() => handleOnClose()}
            className={classes.paper}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Grid item xs={12} md={3}>
              <FormControl
                name={name}
                component="fieldset"
                className="px-5 py-3"
              >
                <FormGroup>
                  <div className="whitespace-nowrap text-primary text-14 font-normal">
                    {!isImportedPage ? (
                      <FormControlLabel
                        control={
                          <CheckBox
                            checked={isSelectedAll}
                            onChange={(e) => {
                              handleSelectedAll(e.target.checked)
                            }}
                            indeterminate={countSelected > 0 && !isSelectedAll}
                            name={`${name}-select-all-checkbox`}
                            disabled={disabled}
                          />
                        }
                        label={formatMessage({
                          id: 'recipient.selection.everyone',
                        })}
                      />
                    ) : null}
                  </div>
                  {optionsArr
                    ?.filter(({ type }) =>
                      isImportedPage ? type !== SUBSCRIBER_WITHOUT_GROUP : true,
                    )
                    ?.map(({ id, tags, type }) => (
                      <div
                        key={id}
                        className="whitespace-nowrap text-primary text-14 font-normal"
                      >
                        <FormControlLabel
                          control={
                            <CheckBox
                              checked={optionsObj[id].parentChecked}
                              indeterminate={optionsObj[id].indeterminateParent}
                              onChange={(e) =>
                                handleSelectedParent(id, e.target.checked)
                              }
                              name={`${optionsObj[id].name}-checkbox`}
                              disabled={disabled}
                            />
                          }
                          label={optionsObj[id].name}
                        />
                        {type !== SUBSCRIBER_WITHOUT_GROUP ? (
                          <div className="ml-10">
                            {tags?.map(({ id: tagId }) => (
                              <div
                                key={`${optionsObj[id]['tags'][tagId].name}-child-checkbox`}
                              >
                                <FormControlLabel
                                  control={
                                    <CheckBox
                                      checked={
                                        optionsObj[id]['tags'][tagId]
                                          .childrenChecked
                                      }
                                      onChange={(e) =>
                                        handleSelectedChild(
                                          id,
                                          tagId,
                                          e.target.checked,
                                        )
                                      }
                                      name={`${optionsObj[id]['tags'][tagId].name}-checkbox`}
                                      disabled={disabled}
                                    />
                                  }
                                  label={optionsObj[id]['tags'][tagId].name}
                                />
                              </div>
                            )) ?? null}
                          </div>
                        ) : null}
                      </div>
                    )) ?? null}
                </FormGroup>
              </FormControl>
            </Grid>
          </Popover>
        </>
      )}
    </>
  )
}

/**
 * Use for future performance optimization when we don't use Redux store for groups
 * Searchable selector data flow (feature-flagged):
 *
 * 1. `useSelectGroup` (React Query) fetches paginated groups based on keyword.
 * 2. User picks tags inside the popover; selections live in local hook state.
 * 3. `convertSelectedTagsToGroupsFormat()` maps tag ids back to Formik shape
 *    `{ groups: [], tags: { [groupId]: [tagId] } }` so downstream filters work.
 * 4. On close, chip labels rely on `convertValueToTagIds()` +
 *    `getSubgroupNamesFromTagIds()` to resolve names from the Redux group list.
 */
function PaginatedSearchableGroupsSelector(props) {
  const {
    onChange,
    disabled = false,
    className,
    name,
    value,
    isImportedPage = false,
  } = props
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const groups = useSelector((state) => state.groups.groupList)

  const [isOpen, setIsOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [optionsArr, setOptionsArr] = useState([])
  const [optionsObj, setOptionsObj] = useState({})
  const [initialOptionsObj, setInitialOptionsObj] = useState({})
  const [selectedSubgroupNames, setSelectedSubgroupNames] = useState([])
  const {
    state: { groups: asyncGroups, selectedTags, isLoading, meta, isLoadmore },
    onFetchGroups,
    onDefaultValue,
    setSelectedTags,
    handleParentCheckboxChange,
    handleTagCheckboxChange,
    loadMore,
    onSearchChange,
    clearState,
    checkAllByGroup,
  } = useSelectGroup()

  const { countSelected, isSelectedAll } = useMemo(() => {
    const countAll = Object.values(optionsObj).filter(
      (parent) =>
        parent.parentChecked ||
        Object.values(parent.tags).filter((child) => child.childrenChecked)
          .length,
    ).length
    return {
      countSelected: countAll,
      isSelectedAll: countAll === Object.keys(optionsObj).length,
    }
  }, [optionsObj])

  const handleLabel = useCallback(() => {
    const onlyUserGroups = groups?.filter(
      (group) => group.type === GROUP_CREATED_BY_USER,
    )
    if (!onlyUserGroups?.length && isImportedPage) {
      return formatMessage({
        id: 'group.notAvailable',
      })
    }

    if (selectedSubgroupNames.length > 0) {
      if (selectedSubgroupNames.length === 1) {
        return selectedSubgroupNames[0]
      }
      return `${selectedSubgroupNames[0]}...`
    }

    if (isSelectedAll || countSelected === 0) {
      return isImportedPage
        ? formatMessage({
            id: 'subscriber.importCsv.group.label',
          })
        : formatMessage({
            id: 'statuse.all',
          })
    } else {
      let results = []

      for (const { id, name, tags } of optionsArr) {
        const parent = optionsObj[id]
        if (parent.parentChecked) {
          results.push(name)
        } else if (!parent.parentChecked && parent.indeterminateParent) {
          for (const { id: tagId, name } of tags || []) {
            if (optionsObj[id]['tags'][tagId].childrenChecked) {
              results.push(name)
            }
            if (results.length === 2) break
          }
        }
        if (results.length === 2) break
      }
      return results.length === 1 ? results[0] : `${results[0]}...`
    }
  }, [
    countSelected,
    formatMessage,
    isImportedPage,
    isSelectedAll,
    optionsArr,
    optionsObj,
    selectedSubgroupNames,
  ])

  const convertSelectedTagsToGroupsFormat = useCallback(() => {
    const preservedGroups =
      value?.groups && Array.isArray(value.groups) ? value.groups : []

    if (!selectedTags || selectedTags.length === 0) {
      return { groups: preservedGroups, tags: {} }
    }

    const tagsByGroup = {}

    if (groups && Array.isArray(groups)) {
      groups.forEach((group) => {
        if (group.tags && Array.isArray(group.tags)) {
          const selectedTagIdsInGroup = group.tags
            .filter((tag) => selectedTags.includes(tag.id))
            .map((tag) => tag.id)

          if (selectedTagIdsInGroup.length > 0) {
            tagsByGroup[group.id] = selectedTagIdsInGroup
          }
        }
      })
    }

    return { groups: preservedGroups, tags: tagsByGroup }
  }, [groups, selectedTags, value])

  const normalizeValue = useCallback((val) => {
    if (!val) {
      return { groups: [], tags: {} }
    }
    return {
      groups: Array.isArray(val.groups) ? val.groups : [],
      tags: val.tags && typeof val.tags === 'object' ? val.tags : {},
    }
  }, [])

  const convertValueToTagIds = useCallback((val) => {
    if (!val || !val.tags || typeof val.tags !== 'object') {
      return []
    }
    return Object.values(val.tags).flat()
  }, [])

  const getSubgroupNamesFromTagIds = useCallback((tagIds, groupsData) => {
    if (
      !tagIds ||
      tagIds.length === 0 ||
      !groupsData ||
      groupsData.length === 0
    ) {
      return []
    }

    const subgroupNames = []

    groupsData.forEach((group) => {
      if (group.tags && Array.isArray(group.tags)) {
        group.tags.forEach((tag) => {
          if (tagIds.includes(tag.id)) {
            subgroupNames.push(tag.name)
          }
        })
      }
    })

    return subgroupNames
  }, [])

  const handleOnClose = useCallback(() => {
    const convertedValue = convertSelectedTagsToGroupsFormat()
    const normalizedValue = normalizeValue(value)
    const normalizedConvertedValue = normalizeValue(convertedValue)

    if (!equals(normalizedValue, normalizedConvertedValue)) {
      onChange(convertedValue)
    }

    const tagIds = convertValueToTagIds(convertedValue)
    if (tagIds.length > 0 && groups && groups.length > 0) {
      const subgroupNames = getSubgroupNamesFromTagIds(tagIds, groups)
      setSelectedSubgroupNames(subgroupNames)
    } else {
      setSelectedSubgroupNames([])
    }

    setIsOpen(false)
    setAnchorEl(null)
  }, [
    onChange,
    value,
    convertSelectedTagsToGroupsFormat,
    convertValueToTagIds,
    getSubgroupNamesFromTagIds,
    groups,
    normalizeValue,
  ])

  useEffect(() => {
    const formattedGroup = getGroupOptions(formatMessage, groups)
    const renewGroups = handleOptionsFilterGroups(formattedGroup)
    setOptionsArr(renewGroups)
    const obj = reduceArrToObj(renewGroups)
    setOptionsObj(obj)
    setInitialOptionsObj(obj)
  }, [formatMessage, groups])

  useEffect(() => {
    if (isEmpty(value.groups) && isEmpty(value.tags)) {
      setOptionsObj(initialOptionsObj)
    }
  }, [initialOptionsObj, value])

  useEffect(() => {
    const tagIds =
      isOpen && selectedTags && selectedTags.length > 0
        ? selectedTags
        : value && value.tags && Object.keys(value.tags).length > 0
          ? convertValueToTagIds(value)
          : []

    if (tagIds.length > 0 && groups && groups.length > 0) {
      const subgroupNames = getSubgroupNamesFromTagIds(tagIds, groups)
      setSelectedSubgroupNames(subgroupNames)
    } else {
      setSelectedSubgroupNames([])
    }
  }, [
    isOpen,
    selectedTags,
    value,
    groups,
    convertValueToTagIds,
    getSubgroupNamesFromTagIds,
  ])

  useEffect(() => {
    if (isOpen) {
      onFetchGroups({
        groups: [],
        page: 1,
        limit: PAGE_LIMIT,
        search: '',
      })
      const tagIds = convertValueToTagIds(value)
      onDefaultValue(tagIds)
    } else {
      clearState()
    }
  }, [
    clearState,
    isOpen,
    onDefaultValue,
    onFetchGroups,
    value,
    convertValueToTagIds,
  ])

  return (
    <>
      {isEmpty(groups) || isEmpty(optionsObj) ? null : (
        <>
          {isImportedPage ? (
            <div className="grid gap-2">
              <div className="text-primary text-14 font-semibold">
                {formatMessage({ id: 'subscriber.groupsSubgroups.title' })}
              </div>
              <Chip
                className={clsx(classes.outlinedChip)}
                label={
                  <Typography className="flex items-center gap-2">
                    <div className="mx-3 font-medium">{handleLabel()}</div>
                    <Icon fontSize="small" className="text-primary">
                      {isOpen ? 'expand_less' : 'expand_more'}
                    </Icon>
                  </Typography>
                }
                onClick={(event) => {
                  setIsOpen(true)
                  setAnchorEl(event.currentTarget)
                }}
                disabled={disabled}
              />
            </div>
          ) : (
            <Chip
              className={clsx(classes.chip, className)}
              label={
                <Typography className="flex items-center">
                  {formatMessage({ id: 'subscriber.label.groups' })}:
                  <strong className="mx-3">{handleLabel()}</strong>
                  <Icon fontSize="small" className="text-primary">
                    {isOpen ? 'expand_less' : 'expand_more'}
                  </Icon>
                </Typography>
              }
              onClick={(event) => {
                setIsOpen(true)
                setAnchorEl(event.currentTarget)
              }}
              disabled={disabled}
            />
          )}

          <Popover
            id={`${name}-paginated-groups-selector-popover`}
            open={isOpen}
            anchorEl={anchorEl}
            onClose={() => handleOnClose()}
            className={classes.paper}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Grid item xs={12} md={3} className="p-1">
              <GroupSelectorContent
                id={`${name}-group-selector-content`}
                groups={asyncGroups}
                selectedTags={selectedTags}
                isLoading={isLoading}
                meta={meta}
                isLoadmore={isLoadmore}
                onSearchChange={onSearchChange}
                handleParentCheckboxChange={handleParentCheckboxChange}
                handleTagCheckboxChange={handleTagCheckboxChange}
                checkAllByGroup={checkAllByGroup}
                loadMore={loadMore}
                variant="dropdown"
                setSelectedTags={setSelectedTags}
              />
            </Grid>
          </Popover>
        </>
      )}
    </>
  )
}

function SearchableGroupsSelector(props) {
  const {
    onChange,
    disabled = false,
    className,
    name,
    value,
    isImportedPage = false,
  } = props
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const groups = useSelector((state) => state.groups.groupList)

  const [isOpen, setIsOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedTags, setSelectedTags] = useState([])
  const [searchKeyword, setSearchKeyword] = useState('')

  const MAX_LABEL_LENGTH = 25

  const groupsWithFilteredTags = useMemo(() => {
    if (!groups?.length) {
      return []
    }

    const keyword = searchKeyword?.toLowerCase().trim()
    if (!keyword) {
      return groups
    }

    return groups
      .filter((group) => {
        const groupNameMatch = group.name?.toLowerCase().includes(keyword)
        const tagMatch = group.tags?.some((tag) =>
          tag.name?.toLowerCase().includes(keyword),
        )
        return groupNameMatch || tagMatch
      })
      .map((group) => {
        if (group.name?.toLowerCase().includes(keyword)) {
          return group
        }
        return {
          ...group,
          tags: group.tags?.filter((tag) =>
            tag.name?.toLowerCase().includes(keyword),
          ),
        }
      })
  }, [groups, searchKeyword])

  const convertValueToTagIds = useCallback((val) => {
    if (!val?.tags || typeof val.tags !== 'object') {
      return []
    }
    return Object.values(val.tags).flat()
  }, [])

  const convertSelectedTagsToGroupsFormat = useCallback(() => {
    const preservedGroups = Array.isArray(value?.groups) ? value.groups : []

    if (!selectedTags?.length) {
      return { groups: preservedGroups, tags: {} }
    }

    const tagsByGroup = {}
    const selectedTagsSet = new Set(selectedTags)

    groups?.forEach((group) => {
      if (!group.tags?.length) return

      const selectedTagIdsInGroup = group.tags
        .filter((tag) => selectedTagsSet.has(tag.id))
        .map((tag) => tag.id)

      if (selectedTagIdsInGroup.length > 0) {
        tagsByGroup[group.id] = selectedTagIdsInGroup
      }
    })

    return { groups: preservedGroups, tags: tagsByGroup }
  }, [groups, selectedTags, value])

  const handleParentCheckboxChange = useCallback(
    (itemId) => {
      const group = groups.find((item) => item.id === itemId)
      if (!group) return

      const tagIds =
        group.tags && Array.isArray(group.tags)
          ? group.tags.map((tag) => tag.id)
          : []

      const isContained = tagIds.every((tagId) => selectedTags.includes(tagId))

      const updatedTags = isContained
        ? selectedTags.filter((id) => !tagIds.includes(id))
        : [
            ...selectedTags,
            ...tagIds.filter((id) => !selectedTags.includes(id)),
          ]

      setSelectedTags(updatedTags)
    },
    [groups, selectedTags],
  )

  const handleTagCheckboxChange = useCallback(
    (tagId) => {
      const isTagSelected = selectedTags.includes(tagId)

      const updatedTags = isTagSelected
        ? selectedTags.filter((id) => id !== tagId)
        : [...selectedTags, tagId]

      setSelectedTags(updatedTags)
    },
    [selectedTags],
  )

  const checkAllByGroup = useCallback(
    (groupId) => {
      const group = groups.find((group) => group.id === groupId)
      if (!group) return false

      const tagIds = group?.tags?.map((tag) => tag.id) ?? []
      return tagIds.length > 0
        ? tagIds.every((element) => selectedTags.includes(element))
        : false
    },
    [groups, selectedTags],
  )

  const label = useMemo(() => {
    const onlyUserGroups = groups?.filter(
      (group) => group.type === GROUP_CREATED_BY_USER,
    )

    if (!onlyUserGroups?.length && isImportedPage) {
      return formatMessage({ id: 'group.notAvailable' })
    }

    if (selectedTags.length > 0 && groups?.length) {
      const tagNames = []
      groups.forEach((group) => {
        group.tags?.forEach((tag) => {
          if (selectedTags.includes(tag.id)) {
            tagNames.push(tag.name)
          }
        })
      })

      if (tagNames.length > 0) {
        const labelText = tagNames.join(', ')
        return labelText.length > MAX_LABEL_LENGTH
          ? `${labelText.substring(0, MAX_LABEL_LENGTH)}...`
          : labelText
      }
    }

    return isImportedPage
      ? formatMessage({ id: 'subscriber.importCsv.group.label' })
      : formatMessage({ id: 'statuse.all' })
  }, [groups, selectedTags, isImportedPage, formatMessage])

  const handleOnClose = useCallback(() => {
    const convertedValue = convertSelectedTagsToGroupsFormat()

    const currentTags = convertValueToTagIds(value)
    const newTags = convertValueToTagIds(convertedValue)

    if (!equals(currentTags, newTags)) {
      onChange(convertedValue)
    }

    setIsOpen(false)
    setAnchorEl(null)
    setSearchKeyword('')
  }, [onChange, value, convertSelectedTagsToGroupsFormat, convertValueToTagIds])

  const onSearchChange = useCallback(({ keyword }) => {
    setSearchKeyword(keyword || '')
  }, [])

  // Sync selected tags with chip label, reset search on open
  useEffect(() => {
    const tagIds = convertValueToTagIds(value)
    setSelectedTags(tagIds)

    if (isOpen) {
      setSearchKeyword('')
    }
  }, [isOpen, value, convertValueToTagIds])

  if (isEmpty(groups)) {
    return null
  }

  const meta = { currentPage: 1, lastPage: 1 }

  return (
    <>
      {isImportedPage ? (
        <div className="grid gap-2">
          <div className="text-primary text-14 font-semibold">
            {formatMessage({ id: 'subscriber.groupsSubgroups.title' })}
          </div>
          <Chip
            className={clsx(classes.outlinedChip)}
            label={
              <Typography className="flex items-center gap-2">
                <div className="mx-3 font-medium">{label}</div>
                <Icon fontSize="small" className="text-primary">
                  {isOpen ? 'expand_less' : 'expand_more'}
                </Icon>
              </Typography>
            }
            onClick={(event) => {
              setIsOpen(true)
              setAnchorEl(event.currentTarget)
            }}
            disabled={disabled}
          />
        </div>
      ) : (
        <Chip
          className={clsx(classes.chip, className)}
          label={
            <Typography className="flex items-center">
              {formatMessage({ id: 'subscriber.label.groups' })}:
              <strong className="mx-3">{label}</strong>
              <Icon fontSize="small" className="text-primary">
                {isOpen ? 'expand_less' : 'expand_more'}
              </Icon>
            </Typography>
          }
          onClick={(event) => {
            setIsOpen(true)
            setAnchorEl(event.currentTarget)
          }}
          disabled={disabled}
        />
      )}

      <Popover
        id={`${name}-searchable-groups-selector-popover`}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleOnClose}
        className={classes.paper}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Grid item xs={12} md={3} className="p-1">
          <GroupSelectorContent
            id={`${name}-group-selector-content`}
            groups={groupsWithFilteredTags}
            selectedTags={selectedTags}
            isLoading={false}
            meta={meta}
            isLoadmore={false}
            onSearchChange={onSearchChange}
            handleParentCheckboxChange={handleParentCheckboxChange}
            handleTagCheckboxChange={handleTagCheckboxChange}
            checkAllByGroup={checkAllByGroup}
            loadMore={() => {}}
            variant="dropdown"
            setSelectedTags={setSelectedTags}
          />
        </Grid>
      </Popover>
    </>
  )
}

export default function GroupsSelector(props) {
  const enableSearchableGroupsSelector = useFlag(
    'mes_5076_searchable_crm_group_filter',
  )

  return enableSearchableGroupsSelector ? (
    <SearchableGroupsSelector {...props} />
  ) : (
    <LegacyGroupsSelector {...props} />
  )
}

GroupsSelector.propTypes = {
  name: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  className: PropTypes.string,
  value: PropTypes.shape({
    groups: PropTypes.array,
    tags: PropTypes.object,
  }),
}
